import { css } from "dx-styles";
import { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";

import { ApiError, api } from "../api/client";
import type { CollectionDetail, CollectionSummary, ImportResponse, Revision } from "../api/types";
import { PublishDialog } from "../components/PublishDialog";
import {
  CopyButton,
  Modal,
  button,
  card,
  errorText,
  heading,
  layout,
  mono,
  muted,
  row,
  spread,
  stack,
  subheading,
} from "../components/primitives";
import { kilobytes, relativeTime, shareLink, subscribeDeepLink } from "../lib/format";
import { tokens } from "../theme/tokens";

const table = css({
  width: "100%",
  borderCollapse: "collapse",
  "& th": {
    textAlign: "start",
    fontSize: "12px",
    textTransform: "uppercase",
    letterSpacing: "0.06em",
    color: tokens.color.pencil,
    padding: `${tokens.space.sm} ${tokens.space.sm}`,
    borderBottom: `1px solid ${tokens.color.rule}`,
  },
  "& td": {
    padding: tokens.space.sm,
    borderBottom: `1px solid ${tokens.color.rule}`,
    verticalAlign: "top",
  },
  "& tr:last-child td": { borderBottom: "none" },
});

const numeric = css({ textAlign: "end", whiteSpace: "nowrap" });

const state = css({
  display: "inline-flex",
  alignItems: "center",
  gap: tokens.space.xs,
  fontSize: "12px",
  fontWeight: 600,
  padding: `2px ${tokens.space.sm}`,
  borderRadius: "999px",
  border: `1px solid ${tokens.color.rule}`,
  backgroundColor: tokens.color.kraft,
});

const dropZone = css({
  border: `1px dashed ${tokens.color.rule}`,
  borderRadius: tokens.radius.md,
  padding: tokens.space.lg,
  textAlign: "center",
  backgroundColor: tokens.color.paper,
});

const dropZoneActive = css({ borderColor: tokens.color.olive, backgroundColor: tokens.color.kraft });

const emptyGrid = css({
  display: "grid",
  gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
  gap: tokens.space.md,
});

interface Props {
  readonly onSignedOut: () => void;
}

/** The `name` key, if this text is a bundle at all. */
function bundleName(text: string): string | undefined {
  try {
    const parsed = JSON.parse(text) as { name?: unknown };
    return typeof parsed.name === "string" && parsed.name.trim().length > 0 ? parsed.name : undefined;
  } catch {
    return undefined;
  }
}

function plural(count: number, singular: string, plural: string): string {
  return `${count} ${count === 1 ? singular : plural}`;
}

export function Collections({ onSignedOut }: Props) {
  const [collections, setCollections] = useState<readonly CollectionSummary[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [importReport, setImportReport] = useState<ImportResponse | null>(null);
  const [publishTarget, setPublishTarget] = useState<CollectionSummary | null>(null);
  const [shareTarget, setShareTarget] = useState<CollectionDetail | null>(null);
  const [revisionsFor, setRevisionsFor] = useState<{ readonly id: string; readonly name: string } | null>(null);
  const [revisions, setRevisions] = useState<readonly Revision[]>([]);
  const [dragging, setDragging] = useState(false);
  const [cloning, setCloning] = useState(false);
  const navigate = useNavigate();
  const fileInput = useRef<HTMLInputElement>(null);

  const reload = useCallback(async () => {
    try {
      setCollections(await api.collections());
      setError(null);
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) {
        onSignedOut();
        return;
      }
      setError(caught instanceof Error ? caught.message : "Could not load collections.");
    }
  }, [onSignedOut]);

  useEffect(() => {
    void reload();
  }, [reload]);

  const importFile = useCallback(
    async (file: File) => {
      setError(null);
      try {
        const text = await file.text();
        // The bundle's own name wins: it is what the author published it as. The
        // file name is a fallback for bundles that carry none.
        const response = await api.importBundle(text, bundleName(text) ?? file.name.replace(/\.json$/i, ""));
        setImportReport(response);
        await reload();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "That file could not be imported.");
      }
    },
    [reload],
  );

  const openShare = useCallback(async (id: string) => {
    setError(null);
    try {
      setShareTarget(await api.collection(id));
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not read that collection.");
    }
  }, []);

  const openRevisions = useCallback(async (collection: CollectionSummary) => {
    setError(null);
    try {
      setRevisions(await api.revisions(collection.id));
      setRevisionsFor({ id: collection.id, name: collection.name });
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not read the revision history.");
    }
  }, []);

  const rollback = useCallback(
    async (revisionNumber: number) => {
      if (!revisionsFor) return;
      setBusyId(revisionsFor.id);
      try {
        await api.rollback(revisionsFor.id, revisionNumber);
        setRevisions(await api.revisions(revisionsFor.id));
        await reload();
      } catch (caught) {
        setError(caught instanceof Error ? caught.message : "Rollback failed.");
      } finally {
        setBusyId(null);
      }
    },
    [reload, revisionsFor],
  );

  return (
    <main className={layout}>
      <div className={spread}>
        <div className={stack}>
          <h1 className={heading}>Collections</h1>
          <p className={muted}>
            Each collection is one subscription link. Templates inside it can inherit from each other.
          </p>
        </div>
        <div className={row}>
          <input
            ref={fileInput}
            type="file"
            accept="application/json,.json"
            hidden
            onChange={(event) => {
              const file = event.target.files?.[0];
              if (file) void importFile(file);
              event.target.value = "";
            }}
          />
          <button type="button" className={button()} onClick={() => fileInput.current?.click()}>
            Import bundle…
          </button>
          <button
            type="button"
            className={button({ tone: "primary" })}
            onClick={() => {
              void (async () => {
                try {
                  await api.createCollection("New collection");
                  await reload();
                } catch (caught) {
                  setError(caught instanceof Error ? caught.message : "Could not create a collection.");
                }
              })();
            }}
          >
            New collection
          </button>
          <button
            type="button"
            className={button({ size: "sm" })}
            onClick={() => {
              void (async () => {
                await api.logout().catch(() => undefined);
                onSignedOut();
              })();
            }}
          >
            Sign out
          </button>
        </div>
      </div>

      {error ? <p className={errorText}>{error}</p> : null}

      <div
        className={dragging ? `${dropZone} ${dropZoneActive}` : dropZone}
        onDragOver={(event) => {
          event.preventDefault();
          setDragging(true);
        }}
        onDragLeave={() => setDragging(false)}
        onDrop={(event) => {
          event.preventDefault();
          setDragging(false);
          const file = event.dataTransfer.files?.[0];
          if (file) void importFile(file);
        }}
      >
        Drop a bundle JSON here — exported from the app or generated by a script. Item ids are kept, so
        re-importing updates instead of duplicating.
      </div>

      {collections === null ? (
        <p className={muted}>Loading…</p>
      ) : collections.length === 0 ? (
        <section className={stack}>
          <div className={stack}>
            <h2 className={subheading}>No collections yet</h2>
            <p className={muted}>
              A collection holds templates and the items they reference, and publishes to one link your phone
              subscribes to. Start from nothing, bring a bundle you already have, or take the sample apart to
              see how rules work.
            </p>
          </div>
          <div className={emptyGrid}>
            <div className={card}>
              <div className={stack}>
                <h3 className={subheading}>Start blank</h3>
                <p className={muted}>
                  An empty collection with one template. You add parameters, then rules that pull items out of
                  your library.
                </p>
                <button
                  type="button"
                  className={button()}
                  onClick={() => {
                    void (async () => {
                      try {
                        const created = await api.createCollection("New collection");
                        navigate(`/collections/${created.id}`);
                      } catch (caught) {
                        setError(caught instanceof Error ? caught.message : "Could not create a collection.");
                      }
                    })();
                  }}
                >
                  Create collection
                </button>
              </div>
            </div>

            <div className={card}>
              <div className={stack}>
                <h3 className={subheading}>Import a bundle</h3>
                <p className={muted}>
                  Drop a JSON exported from the app — or generated by a script. Item ids are kept, so
                  re-importing updates instead of duplicating.
                </p>
                <button type="button" className={button()} onClick={() => fileInput.current?.click()}>
                  Choose file…
                </button>
              </div>
            </div>

            <div className={card}>
              <div className={stack}>
                <h3 className={subheading}>Start from the sample</h3>
                <p className={muted}>
                  The First Aid Kit: three parameters, formulas with min and ceil, a high-altitude conditional
                  block, a group rule by tag and an override. It publishes as it is.
                </p>
                <button
                  type="button"
                  className={button({ tone: "primary" })}
                  disabled={cloning}
                  onClick={() => {
                    setCloning(true);
                    void (async () => {
                      try {
                        const response = await api.cloneSample();
                        navigate(`/collections/${response.collection.id}`);
                      } catch (caught) {
                        setError(caught instanceof Error ? caught.message : "Could not clone the sample.");
                      } finally {
                        setCloning(false);
                      }
                    })();
                  }}
                >
                  {cloning ? "Cloning…" : "Clone the sample"}
                </button>
              </div>
            </div>
          </div>
          <p className={muted}>
            Publishing changes what subscribers get: the app regenerates their checklists automatically and
            keeps what they have already packed. Nothing reaches them until you press Publish.
          </p>
        </section>
      ) : (
        <table className={table}>
          <thead>
            <tr>
              <th scope="col">Collection</th>
              <th scope="col" className={numeric}>
                Templates
              </th>
              <th scope="col" className={numeric}>
                Items
              </th>
              <th scope="col" className={numeric}>
                Subscribers
              </th>
              <th scope="col">Published</th>
              <th scope="col">
                <span aria-hidden="true">Actions</span>
              </th>
            </tr>
          </thead>
          <tbody>
            {collections.map((collection) => (
              <tr key={collection.id}>
                <td>
                  <div className={stack}>
                    <div className={row}>
                      <strong>{collection.name}</strong>
                      <span className={state}>
                        {collection.isPublished
                          ? `rev ${collection.publishedRevision ?? "?"}`
                          : collection.publishedRevision
                            ? "unpublished"
                            : "never published"}
                      </span>
                      {collection.hasAccessCode ? <span className={state}>code</span> : null}
                    </div>
                    {collection.description ? <span className={muted}>{collection.description}</span> : null}
                  </div>
                </td>
                <td className={numeric}>{collection.templateCount}</td>
                <td className={numeric}>{collection.itemCount}</td>
                <td className={numeric}>
                  {collection.subscriberEstimate > 0 ? `~${collection.subscriberEstimate}` : "—"}
                </td>
                <td>{relativeTime(collection.publishedAt)}</td>
                <td>
                  <div className={row}>
                    <button
                      type="button"
                      className={button({ tone: "primary", size: "sm" })}
                      onClick={() => setPublishTarget(collection)}
                    >
                      Publish
                    </button>
                    <a className={button({ size: "sm" })} href={`/collections/${collection.id}`}>
                      Edit
                    </a>
                    <button
                      type="button"
                      className={button({ size: "sm" })}
                      onClick={() => void openShare(collection.id)}
                    >
                      Link
                    </button>
                    <button
                      type="button"
                      className={button({ size: "sm" })}
                      onClick={() => void openRevisions(collection)}
                    >
                      History
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      <p className={muted}>
        Subscriber counts are approximate — distinct clients that fetched the bundle in the last 30 days.
      </p>

      <PublishDialog
        collectionId={publishTarget?.id ?? ""}
        collectionName={publishTarget?.name ?? ""}
        open={publishTarget !== null}
        onOpenChange={(open) => {
          if (!open) setPublishTarget(null);
        }}
        onPublished={() => void reload()}
      />

      <Modal
        open={importReport !== null}
        onOpenChange={(open) => {
          if (!open) setImportReport(null);
        }}
        title="Imported"
        description={importReport ? `“${importReport.collection.name}” is now a draft.` : undefined}
      >
        {importReport ? (
          <div className={stack}>
            {importReport.report.hardFailures.length > 0 ? (
              <div className={stack}>
                <strong className={errorText}>Publishing is blocked until these are fixed:</strong>
                <ul>
                  {importReport.report.hardFailures.map((issue) => (
                    <li key={`${issue.code}-${issue.message}`}>{issue.message}</li>
                  ))}
                </ul>
              </div>
            ) : (
              <span>The bundle passes the publish gate.</span>
            )}
            {importReport.report.warnings.length > 0 ? (
              <details>
                <summary>{plural(importReport.report.warnings.length, "warning", "warnings")}</summary>
                <ul>
                  {importReport.report.warnings.slice(0, 20).map((issue) => (
                    <li key={`${issue.code}-${issue.message}`}>{issue.message}</li>
                  ))}
                </ul>
              </details>
            ) : null}
            {importReport.conflicts.length > 0 ? (
              <details>
                <summary>
                  {plural(importReport.conflicts.length, "item id", "item ids")} already in use
                </summary>
                <ul>
                  {importReport.conflicts.map((conflict) => (
                    <li key={conflict.itemId}>{conflict.detail}</li>
                  ))}
                </ul>
              </details>
            ) : null}
            <button type="button" className={button({ tone: "primary" })} onClick={() => setImportReport(null)}>
              Done
            </button>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={shareTarget !== null}
        onOpenChange={(open) => {
          if (!open) setShareTarget(null);
        }}
        title="Subscription link"
        description={shareTarget?.name}
      >
        {shareTarget ? (
          <div className={stack}>
            <code className={mono}>{shareLink(shareTarget.bundleUrl, shareTarget.accessCode)}</code>
            <div className={row}>
              <CopyButton value={shareLink(shareTarget.bundleUrl, shareTarget.accessCode)} label="Copy link" />
              <a
                className={button({ size: "sm" })}
                href={subscribeDeepLink(shareTarget.bundleUrl, shareTarget.accessCode)}
              >
                Open in app
              </a>
              <button
                type="button"
                className={button({ size: "sm" })}
                onClick={() => {
                  void (async () => {
                    const response = await api.rotateAccessCode(shareTarget.id, shareTarget.accessCode != null);
                    setShareTarget({ ...shareTarget, accessCode: response.accessCode ?? null });
                    await reload();
                  })();
                }}
              >
                {shareTarget.accessCode ? "Remove access code" : "Add access code"}
              </button>
            </div>
            <p className={muted}>
              {shareTarget.accessCode
                ? "The code travels in the link's fragment, so it never reaches server logs. Rotating it does not republish: subscribers keep their revision."
                : "Anyone with this link can subscribe. Add a code to gate it."}
            </p>
          </div>
        ) : null}
      </Modal>

      <Modal
        open={revisionsFor !== null}
        onOpenChange={(open) => {
          if (!open) setRevisionsFor(null);
        }}
        title="Revisions"
        description={revisionsFor?.name}
      >
        <div className={stack}>
          {revisions.length === 0 ? (
            <span className={muted}>Nothing published yet.</span>
          ) : (
            revisions.map((revision) => (
              <div key={revision.revisionNumber} className={spread}>
                <div className={stack}>
                  <strong>
                    rev {revision.revisionNumber}
                    {revision.isCurrent ? " · serving" : ""}
                  </strong>
                  <span className={muted}>
                    {relativeTime(revision.publishedAt)} · {kilobytes(revision.byteSize)} ·{" "}
                    <span className={mono}>{revision.contentHash.slice(0, 12)}</span>
                  </span>
                </div>
                <button
                  type="button"
                  className={button({ size: "sm" })}
                  disabled={revision.isCurrent || busyId !== null}
                  onClick={() => void rollback(revision.revisionNumber)}
                >
                  Serve this
                </button>
              </div>
            ))
          )}
        </div>
      </Modal>
    </main>
  );
}
